import {Component, DestroyRef, inject, input, OnDestroy, OnInit, output, signal} from '@angular/core';
import {ContactLinkHttpService} from '../../../_auth/contact/contact-link-http.service';
import {
  ContactChallengeState,
  ContactChallengeStateValue,
  ContactLinkChallenge,
  ContactLinkPurpose,
  ContactLinkPurposeValue,
  ContactLinkStep,
  ContactStepKind
} from '../../../_auth/contact/contact-link.models';
import {describeContactLinkError, isChannelUnavailable} from '../../../_auth/contact/contact-link-errors';

/** How often the wizard asks the server whether the bot has heard from the employee yet. */
export const CONTACT_POLL_INTERVAL_MS = 2500;

/** How often the countdown to `expiresUtc` re-renders. */
export const CONTACT_COUNTDOWN_INTERVAL_MS = 1000;

/** How long „Kopiert" stays on the copy button. */
export const CONTACT_COPY_FEEDBACK_MS = 2000;

/**
 * The one guided flow for linking (or re-verifying) a contact channel — used by the lockout screen
 * `ContactLinkingComponent` and, unchanged, by the voluntary „Meine Verknüpfungen" page (#558).
 *
 * ### It renders the SERVER's guide, not Telegram's
 *
 * Everything on screen comes from the challenge's `steps[]`: their order, their wording and their links are the
 * server's (`IContactChannelLinker.Describe()`), and this component only knows how a step of a given **kind**
 * looks — a code to copy, a link to open, a wait, a paragraph. That is the whole reason WhatsApp or SMS can
 * follow as data plus one server-side linker. So: no `channel === 'telegram'` branch belongs in here, ever, and
 * an unknown `kind` renders as plain prose rather than being dropped — a step this build cannot draw is still
 * an instruction the employee needs, and skipping it silently is how a wizard becomes uncompletable against a
 * newer server.
 *
 * ### Completion is asynchronous, so it is polled
 *
 * The employee's half of the flow happens in a messenger, out of the browser's sight. `GET
 * …/challenge/{id}` is therefore polled every {@link CONTACT_POLL_INTERVAL_MS} until the state is terminal.
 * The challenge's own `expiresUtc` bounds that (a ~10 minute TTL server-side), and the countdown is shown
 * rather than hidden because "is this code still good?" is the question somebody staring at an unanswered chat
 * actually has.
 *
 * ### The four terminal states each need their OWN wording
 *
 * `completed`, `declined`, `expired` and "the channel is not available at all" are not four flavours of error:
 * they need four different next actions, and collapsing any two of them produces a dead end. A **declined**
 * share in particular is not a failure of anything — the employee refused, deliberately, and the screen says so
 * and offers to ask again. An **unavailable** channel is the only one with no self-service next step, so it is
 * the one place that names a person instead of offering a retry button that cannot work.
 */
@Component({
  selector: 'app-contact-link-wizard',
  standalone: true,
  templateUrl: './contact-link-wizard.component.html'
})
export class ContactLinkWizardComponent implements OnInit, OnDestroy {
  /** The channel to link, as the server spells it (`telegram`). */
  readonly channel = input.required<string>();
  /** Its display name, for the headings. Falls back to the raw identifier so a label is never empty. */
  readonly channelLabel = input<string>('');
  /** A first link, or proving an existing link is still live. */
  readonly purpose = input<ContactLinkPurposeValue>(ContactLinkPurpose.Link);
  /**
   * Whether the surface hosting this wizard offers a way out.
   *
   * False on the lockout screen while the task blocks: there is nothing to cancel *to*. True everywhere else —
   * the voluntary link on „Meine Verknüpfungen", and a non-blocking task inside its Frist.
   */
  readonly dismissable = input<boolean>(true);

  /** The link was established (or re-verified). Carries the server's `linkId` when it sent one. */
  readonly completed = output<string | null>();
  /** The employee backed out. Only reachable while {@link dismissable}. */
  readonly dismissed = output<void>();

  /** The challenge is being created. */
  readonly starting = signal(true);
  /** The live challenge, once started. */
  readonly challenge = signal<ContactLinkChallenge | null>(null);
  /** The last state the poll reported. Seeded `pending`, which is what a fresh challenge is. */
  readonly state = signal<ContactChallengeStateValue | string>(ContactChallengeState.Pending);
  /** A failure of the START call (the poll's own failures are transient and deliberately not surfaced). */
  readonly errorMessage = signal<string | null>(null);
  /** The channel itself cannot be used — retrying is pointless, so no retry is offered. */
  readonly unavailable = signal(false);
  /** „Kopiert" feedback on the copy button. */
  readonly copied = signal(false);
  /** The clipboard API refused (or does not exist here) — the code is still on screen to select by hand. */
  readonly copyFailed = signal(false);
  /** Whole seconds until `expiresUtc`, or null before a challenge exists. */
  readonly secondsLeft = signal<number | null>(null);

  protected readonly StepKind = ContactStepKind;
  protected readonly ChallengeState = ContactChallengeState;
  protected readonly Purpose = ContactLinkPurpose;

  private readonly contacts = inject(ContactLinkHttpService);
  private readonly destroyRef = inject(DestroyRef);

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private countdownHandle: ReturnType<typeof setInterval> | null = null;
  private copyHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    // Belt and braces beside ngOnDestroy: a fixture destroyed without Angular calling the hook (or a host that
    // swaps this component out mid-poll) would otherwise leave an interval hitting a torn-down service.
    this.destroyRef.onDestroy(() => this.stopTimers());
    this.start();
  }

  ngOnDestroy(): void {
    this.stopTimers();
  }

  /** The steps to render, or an empty list while starting/failed. */
  get steps(): ContactLinkStep[] {
    return this.challenge()?.steps ?? [];
  }

  /** The channel's display name, never empty. */
  get label(): string {
    const given = this.channelLabel();
    if (given) return given;
    const channel = this.channel();
    return channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : 'Kanal';
  }

  /** True while the flow is still waiting on the messenger — the only state the poll runs in. */
  get waiting(): boolean {
    return this.state() === ContactChallengeState.Pending
      || this.state() === ContactChallengeState.AwaitingShareConsent;
  }

  /** The countdown as `m:ss`, or null when there is nothing to count down to. */
  get remaining(): string | null {
    const seconds = this.secondsLeft();
    if (seconds === null || seconds < 0) return null;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  }

  /**
   * Start (or restart) the flow: ask for a challenge, then begin polling and counting down.
   *
   * Also the handler behind „Erneut versuchen" and „Neuen Code anfordern" — a retry and a fresh code are the
   * same act, and giving them one implementation is what stops the second one from quietly diverging.
   */
  start(): void {
    this.stopTimers();
    this.starting.set(true);
    this.errorMessage.set(null);
    this.unavailable.set(false);
    this.copied.set(false);
    this.copyFailed.set(false);
    this.challenge.set(null);
    this.state.set(ContactChallengeState.Pending);
    this.secondsLeft.set(null);

    this.contacts.startChallenge(this.channel(), this.purpose()).subscribe({
      next: challenge => {
        this.starting.set(false);
        this.challenge.set(challenge);
        this.tickCountdown();
        this.pollHandle = setInterval(() => this.poll(), CONTACT_POLL_INTERVAL_MS);
        this.countdownHandle = setInterval(() => this.tickCountdown(), CONTACT_COUNTDOWN_INTERVAL_MS);
      },
      error: err => {
        this.starting.set(false);
        this.unavailable.set(isChannelUnavailable(err));
        this.errorMessage.set(describeContactLinkError(
          err, 'Die Verknüpfung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.'));
      }
    });
  }

  /** Copy the challenge code, with visible confirmation — the step most easily got wrong by retyping. */
  async copyCode(): Promise<void> {
    const code = this.challenge()?.code;
    if (!code) return;

    this.copyFailed.set(false);
    try {
      // Optional-chained: `navigator.clipboard` is undefined on an insecure origin and in some headless
      // browsers, and an unguarded call there throws a TypeError out of a click handler.
      await navigator.clipboard?.writeText(code);
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      this.copied.set(true);
      if (this.copyHandle) clearTimeout(this.copyHandle);
      this.copyHandle = setTimeout(() => this.copied.set(false), CONTACT_COPY_FEEDBACK_MS);
    } catch {
      // Never claim a copy that did not happen: the code is on screen in a selectable block, and the hint
      // tells the reader to take it from there.
      this.copied.set(false);
      this.copyFailed.set(true);
    }
  }

  /** Back out — only offered while {@link dismissable}. */
  dismiss(): void {
    this.stopTimers();
    this.dismissed.emit();
  }

  /**
   * One poll. Terminal states stop the timers; a **failed** poll is deliberately ignored.
   *
   * That last part is the judgement call worth stating: a poll runs every 2.5 s against a network that will
   * occasionally drop one, and turning a single failed read into a visible error would make an otherwise fine
   * flow look broken every few minutes. The challenge's own expiry is the backstop — a poll that never
   * succeeds again ends in `expired`, which the countdown reaches on its own.
   */
  private poll(): void {
    const id = this.challenge()?.challengeId;
    if (!id) return;

    this.contacts.challengeStatus(id).subscribe({
      next: status => this.apply(status?.state, status?.linkId ?? null),
      error: () => { /* transient — see the doc comment */ }
    });
  }

  private apply(state: string | undefined, linkId: string | null): void {
    if (!state) return;
    this.state.set(state);

    if (state === ContactChallengeState.Completed) {
      this.stopTimers();
      this.completed.emit(linkId);
      return;
    }
    if (state === ContactChallengeState.Declined || state === ContactChallengeState.Expired) {
      this.stopTimers();
    }
  }

  /**
   * Re-derive the countdown, and expire the challenge locally when it runs out.
   *
   * Expiring here rather than waiting for the server to say so is what keeps the screen honest for the reader:
   * the code on screen is provably dead the moment its instant passes, and „Neuen Code anfordern" is the only
   * useful thing left. The server agrees the next time it is asked; nothing is decided differently here.
   */
  private tickCountdown(): void {
    const expiresUtc = this.challenge()?.expiresUtc;
    if (!expiresUtc) {
      this.secondsLeft.set(null);
      return;
    }

    const msLeft = new Date(expiresUtc).getTime() - Date.now();
    if (!Number.isFinite(msLeft)) {
      this.secondsLeft.set(null);
      return;
    }

    this.secondsLeft.set(Math.max(0, Math.ceil(msLeft / 1000)));
    if (msLeft <= 0 && this.waiting) {
      this.state.set(ContactChallengeState.Expired);
      this.stopTimers();
    }
  }

  private stopTimers(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
    if (this.countdownHandle) clearInterval(this.countdownHandle);
    if (this.copyHandle) clearTimeout(this.copyHandle);
    this.pollHandle = null;
    this.countdownHandle = null;
    this.copyHandle = null;
  }
}
